<template>
  <div class="home position-relative">
    <info-status-alert />
    <filters
      v-model="form"
      @timezone-changed="updateTimezone"
    >
      <template v-slot:disabledData>
        <div
          class="text-center w-100"
          :class="IS_MOBILE() ? 'pb-1' : 'pt-2'"
        >
          <h3
            v-if="activeTab === 0"
            style="margin-top: 8px;"
          >
            {{ FORMAT_DATE(todayDate.start, 'DD MMM, YYYY') }}
          </h3>
          <h3
            v-if="activeTab === 1"
            style="margin-top: 8px;"
          >
            {{ FORMAT_DATE(yesterday.start, 'DD MMM, YYYY') }}
          </h3>
          <h3
            v-if="activeTab === 2"
            style="margin-top: 8px;"
          >
            {{ FORMAT_DATE_RANGE(todayWeeks.start, todayWeeks.endDate) }}
          </h3>
          <h3
            v-if="activeTab === 3"
            style="margin-top: 8px;"
          >
            {{ FORMAT_DATE_RANGE(todayMonth.start, todayMonth.endDate) }}
          </h3>
          <h3
            v-if="activeTab === 4"
            style="margin-top: 8px;"
          >
            {{ FORMAT_DATE(form.date, 'DD MMM, YYYY') }}
          </h3>
          <h3
            v-if="activeTab === 5"
            style="margin-top: 8px;"
          >
            {{ FORMAT_DATE_RANGE(form.start_date, form.end_date) }}
          </h3>
        </div>
      </template>
    </filters>
    <b-tabs
      id="tour-monitor-users"
      v-model="activeTab"
      pills
    >
      <b-tab
        :title="$t('Today')"
        lazy
        @click="refreshToday"
      >
        <day-view
          id="day-view"
          ref="todayView"
          :key="getUserId"
          class="pt-1"
          :date-range="todayDate"
          :this-week-duration="thisWeekDuration"
          :user-id="getUserId"
          :this-month-duration="thisMonthDuration"
          :time-zone="getTimeZone"
        />
      </b-tab>
      <b-tab
        :title="$t('Yesterday')"
        lazy
      >
        <day-view
          :key="getUserId"
          class="pt-1"
          :date-range="yesterday"
          :this-week-duration="thisWeekDuration"
          :this-month-duration="thisMonthDuration"
          :user-id="getUserId"
          :time-zone="getTimeZone"
        />
      </b-tab>
      <b-tab
        :title="$t('Past 7 days')"
        lazy
      >
        <multiple-day-view
          :key="getUserId"
          :date-range="todayWeeks"
          :user-id="getUserId"
          :time-zone="getTimeZone"
        />
      </b-tab>
      <b-tab
        :title="$t('Past 30 days')"
        lazy
      >
        <multiple-day-view
          :key="getUserId"
          :date-range="todayMonth"
          :user-id="getUserId"
          :time-zone="getTimeZone"
        />
      </b-tab>
      <b-tab
        id="SINGLE_DATE"
        lazy
      >
        <template #title>
          <b-form-datepicker
            :value="form.date"
            button-variant="link"
            :initial-date="TODAY()"
            offset="-110"
            button-only
            @input="form.date = $event"
            @shown="activeTab = 4"
          >
            <template #button-content>
              {{ $t('Date') }}
            </template>
          </b-form-datepicker>
        </template>
        <day-view
          :key="getUserId"
          class="pt-1"
          :date-range="singleDate"
          :this-week-duration="thisWeekDuration"
          :user-id="getUserId"
          :this-month-duration="thisMonthDuration"
          :time-zone="getTimeZone"
        />
      </b-tab>
      <b-tab
        id="DATE_RANGE"
        :title="$t('Date Range')"
        lazy
      >
        <multiple-day-view
          :key="getUserId"
          :date-range="dateRange"
          :user-id="getUserId"
          :time-zone="getTimeZone"
        />
      </b-tab>
    </b-tabs>

    <date-range-picker
      trigger-target="DATE_RANGE___BV_tab_button__"
      :start-date="form.start_date"
      triggers="click"
      :end-date="form.end_date"
      @change="(({ startDate, endDate}) => {
        form.start_date = startDate
        form.end_date = endDate
      })"
    />
  </div>
</template>

<script>
import {
  BTab, BTabs, BFormDatepicker,
} from 'bootstrap-vue'
import useApollo from '@/plugins/graphql/useApollo'
import Filters from '@/views/common/components/Filter.vue'
import { mapActions, mapGetters } from 'vuex'
import { getUserData } from '@/auth/utils'
import DayView from './Dashboard/SingleDay/Index.vue'
import MultipleDayView from './Dashboard/MultipleDay/index.vue'
import {
  checkComponentReady, continueTour, isActiveTourStep, setComponentReady,
} from './tour/tour'
import { memberTourIndexes, ownerTourIndexes } from './tour/tourConstants'
import InfoStatusAlert from './common/components/InfoStatusAlert.vue'
import DateRangePicker from './common/components/DateRangePicker.vue'

export default {
  components: {
    BTab,
    BTabs,
    Filters,
    DayView,
    MultipleDayView,
    InfoStatusAlert,
    DateRangePicker,
    BFormDatepicker,
  },
  data() {
    return {
      self: getUserData(),
      form: {
        date: this.$route.query.date ?? (this.TODAY({ localTime: false })),
        time_zone: localStorage.timezone ?? 'Asia/Kathmandu',
        userId: this.$route.query.userId ?? null,
        start_date: this.TODAY({ localTime: false }),
        end_date: this.TODAY({ localTime: false }),
      },
      thisWeekDuration: 0,
      thisMonthDuration: 0,
      activeTab: this.$route.query.date ? 4 : 0,
      today: this.TODAY({ localTime: false }),
    }
  },
  computed: {
    ...mapGetters('project', ['GET_SELECTED_USER']),
    getUserId() {
      return this.form.userId
    },
    getTimeZone() {
      return this.form.time_zone
    },
    singleDate() {
      return {
        start: `${this.form.date} 00:00:00`,
        end: this.ADD_DAYS(this.form.date, 1, 'YYYY-MM-DD 00:00:00').start,
      }
    },
    todayDate() {
      return {
        start: `${this.today} 00:00:00`,
        end: this.ADD_DAYS(this.today, 1, 'YYYY-MM-DD 00:00:00').start,
      }
    },
    yesterday() {
      const interval = this.YESTERDAY(this.today)
      return {
        start: interval.start,
        end: this.ADD_DAYS(interval.end, 1, 'YYYY-MM-DD 00:00:00').start,
      }
    },
    dateRange() {
      return {
        start: `${this.form.start_date} 00:00:00`,
        end: this.ADD_DAYS(this.form.end_date, 1, 'YYYY-MM-DD 00:00:00').start,
      }
    },
    todayWeeks() {
      const interval = this.SUBTRACT_DAYS(this.today, 6, 'YYYY-MM-DD HH:mm:ss')
      return {
        start: interval.start,
        endDate: interval.end,
        end: this.ADD_DAYS(interval.end, 1, 'YYYY-MM-DD 00:00:00').start,
      }
    },
    todayMonth() {
      const interval = this.SUBTRACT_DAYS(this.today, 29, 'YYYY-MM-DD HH:mm:ss')
      return {
        start: interval.start,
        endDate: interval.end,
        end: this.ADD_DAYS(interval.end, 1, 'YYYY-MM-DD 00:00:00').start,
      }
    },
    isOwner() {
      const ownerRole = this.self.companyRoles.data.find(companyRole => companyRole.role.name === 'owner')
      return ownerRole ? 'owner' : 'member'
    },
  },
  watch: {
    GET_SELECTED_USER: {
      handler(val) {
        if (this.$route.query.userId) this.setSelectedUser(this.$route.query.userId)
        else if (val && val !== this.form.userId) this.form.userId = val
      },
      immediate: true,
    },
    getUserId(val) {
      this.getMonthWeekDuration()
      if (val !== this.GET_SELECTED_USER) this.setSelectedUser(val)
    },
    form: {
      handler(newValue) {
        this.storeFilterQuery('dashboard-filer', { ...newValue })
        this.getMonthWeekDuration()
        this.today = this.TODAY({ localTime: false })
      },
      deep: true,
    },
    activeTab(val) {
      if (val !== 5) {
        this.$root.$emit('bv::hide::popover')
      }
    },
  },

  async created() {
    this.getMonthWeekDuration()

    if (this.$store.state.tour.isTouringPaused) {
      await checkComponentReady()
      continueTour({ step: this.isOwner ? ownerTourIndexes.TOUR_USER_DROPDOWN : memberTourIndexes.TOUR_USER_DROPDOWN })
    }
  },
  methods: {
    ...mapActions('project', ['setSelectedUser']),
    getRecordDuration({ startTime, endTime }) {
      return useApollo.activities
        .getDurations({
          startTime,
          endTime,
          keyword: localStorage.selected_project,
          uuid: this.getUserId,
        })
    },
    getMonthWeekDuration() {
      const week = this.INTERVAL_IN_WEEKS(this.TODAY('YYYY-MM-DD 00:00:00'))
      const month = this.INTERVAL_IN_MONTH(this.TODAY('YYYY-MM-DD 00:00:00'))
      this.getRecordDuration({
        startTime: week.start,
        endTime: week.end,
      }).then(response => {
        this.thisWeekDuration = response.data.me.recordsDuration
      }).finally(() => {
        if (this.checkTourStep) {
          setComponentReady()
        }
      })

      this.getRecordDuration({
        startTime: month.start,
        endTime: month.end,
      }).then(response => {
        this.thisMonthDuration = response.data.me.recordsDuration
      })
    },
    refreshToday() {
      if (this.$refs.todayView) {
        this.$refs.todayView.getUserActivities()
        this.getMonthWeekDuration()
      }
    },
    updateTimezone() {
      if (this.isAfterTODAY(this.form.date)) this.form.date = this.TODAY({ localTime: false })
      if (this.isAfterTODAY(this.form.start_date)) this.form.start_date = this.TODAY({ localTime: false })
      if (this.isAfterTODAY(this.form.end_date)) this.form.end_date = this.TODAY({ localTime: false })
    },
    checkTourStep() {
      return isActiveTourStep(ownerTourIndexes.TOUR_PERMISSIONS_FORM) || isActiveTourStep(ownerTourIndexes.TOUR_HOURS_TRACKED) || isActiveTourStep(memberTourIndexes.TOUR_CHANGE_PROJECT) || isActiveTourStep(memberTourIndexes.TOUR_HOURS_TRACKED)
    },
  },
}
</script>

<style lang="scss">
@media screen and (max-width: 768px) {
  .nav-pills .nav-link {
    padding: 0.5rem 1rem;
    margin-bottom: 8px;
  }
}

#SINGLE_DATE___BV_tab_button__ {
  padding: 0;
  .btn-link {
    padding: 0 !important;
    color: #5e5873 !important;
    font-weight: 400;
    padding: 0.786rem 1.5rem !important;
  }
  &.nav-link {
    &.active .btn-link {
      color: white !important;
    }
  }

  // Caret for the datepicker calendar modal
  .b-calendar-grid {
    position: relative;
  }
}

// Style the calendar dropdown to include a caret pointing to the button
#SINGLE_DATE___BV_tab_button__ .dropdown-menu {
  left: 8px !important;

  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid #3D61AD;
    z-index: 1001;
  }

  &::after {
    content: '';
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid rgba(0, 0, 0, 0.15);
    z-index: 1000;
  }
}

.dark-layout {
  #SINGLE_DATE___BV_tab_button__ {
    .btn-link {
      color: #d0d2d6 !important;
    }
    &.active .btn-link {
    color: white !important;
  }
  }

  // Dark mode caret colors
  #SINGLE_DATE___BV_tab_button__ .dropdown-menu,
  #SINGLE_DATE .b-form-datepicker .dropdown-menu {
    &::before {
      border-bottom-color: #283046;
    }

    &::after {
      border-bottom-color: rgba(255, 255, 255, 0.15);
    }
  }
}
</style>
